import { useEffect, useState } from 'react';
import { Loader2, SlidersHorizontal } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PasosDelAsistente } from '@/components/ui/pasos-del-asistente';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import {
  FrenoDeLaDemo,
  frenoDelError,
  type FrenoDeLaDemoProps,
} from '@/features/mini-prixia/freno-de-la-demo';
import { strategyApplicationsApi } from '../../services/strategy-applications-api';
import {
  BORRADOR_VACIO,
  cuerpoDeAlta,
  faltaEnElPaso,
  valorInicial,
  type BorradorDeEstrategia,
} from './borrador';
import { PERMISO_INICIAL, permisoEnPalabras } from '../permiso-de-estrategia';
import { formatoFecha } from '@/lib/utils/formato-precio';
import { FilaDelResumen, ResumenDelAsistente } from '@/features/compite/ui/resumen-del-asistente';
import { Paso1Alcance, type DesdeAnalisis } from './paso-1-alcance';
import { METODOS, Paso2Metodo } from './paso-2-metodo';
import { Paso3Cuando, resumenParaElSubtitulo } from './paso-3-cuando';
import { Paso4Resultados } from './paso-4-resultados';
import { BOTON_DEGRADADO } from '@/features/compite/ui/boton-degradado';

/** Agregados del 29-sep (A4): «Resultados esperados» es el paso 4, y ahí se crea. */
const PASOS = [
  'A quién',
  'Cómo calcula',
  'Cuándo y con qué permiso',
  'Resultados esperados',
] as const;

/** «A quién» de «Tu estrategia», en una línea; `null` mientras no se eligió nada. */
function aQuienEnPalabras(b: BorradorDeEstrategia): string | null {
  if (b.forma === 'PRODUCTOS') {
    if (b.skus.length === 0) return null;
    return b.skus.length === 1 ? '1 producto elegido' : `${b.skus.length} productos elegidos`;
  }
  const partes = [b.categorias.join(', '), b.marcas.join(', ')].filter(Boolean);
  if (partes.length === 0) return null;
  const menos =
    b.excluidos.length > 0
      ? `, menos ${b.excluidos.length === 1 ? '1 excepción' : `${b.excluidos.length} excepciones`}`
      : '';
  return `${partes.join(' · ')}${menos}`;
}

/** «Ponele un nombre…» → «ponele un nombre…»: va después de «Falta:». */
const minusculaInicial = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/**
 * Crear una estrategia en cuatro pasos (plan 3, Tarea 5; el cuarto, «Resultados
 * esperados», de los agregados del 29-sep, A4). Reemplaza al diálogo
 * «Aplicar una estrategia» de la pestaña Estrategias y es el único camino para
 * crear una: el «aplicar a un grupo» de Análisis abre éste con el grupo ya
 * cargado (P3-D4), así todo pasa por propuestas y permisos.
 *
 * `inicial` completa el borrador (Análisis manda el nombre y el alcance;
 * «Duplicar», todo menos el nombre y la fecha de fin — Tarea 6b).
 * `desdeAnalisis` (D4): se abrió desde Análisis por categoría.
 */
export function NuevaEstrategiaDialog({
  abierto,
  onCerrar,
  onCreada,
  inicial,
  desdeAnalisis = null,
  duplicaDe,
}: {
  abierto: boolean;
  onCerrar: () => void;
  onCreada: () => void | Promise<void>;
  inicial?: Partial<BorradorDeEstrategia> | null;
  desdeAnalisis?: DesdeAnalisis | null;
  /**
   * «Duplicar» (Tarea 6b): de cuál es la copia, qué no se pudo copiar
   * (`loQueNoSeCopia`) y si la original no venció, activa o apagada (P3-D8 con P2-D7: el paso 3 lo avisa).
   */
  duplicaDe?: { nombre: string; noSeCopia: string[]; originalActiva: boolean } | null;
}) {
  const { toast } = useToast();
  // Soporte («Entrar como») en una demo sólo mira: no crea estrategias.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  const nuevo = (): BorradorDeEstrategia => {
    const b = { ...BORRADOR_VACIO, ...(inicial ?? {}) };
    return { ...b, valor: inicial?.valor ?? valorInicial(b.metodo) };
  };
  const [b, setB] = useState<BorradorDeEstrategia>(nuevo);
  const [paso, setPaso] = useState<1 | 2 | 3 | 4>(1);
  const [solapados, setSolapados] = useState<number | null>(null);
  const [guardando, setGuardando] = useState(false);
  // Reglas de la demo (343): la estrategia alcanza productos propios; se dice en el cuadro.
  const [freno, setFreno] = useState<FrenoDeLaDemoProps | null>(null);

  // Cada vez que se abre, arranca de cero (o de lo que mandó Análisis).
  useEffect(() => {
    if (!abierto) return;
    setB(nuevo());
    setPaso(1);
    setSolapados(null);
    setFreno(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const cambiar = (c: Partial<BorradorDeEstrategia>) =>
    setB((x) => {
      const siguiente = { ...x, ...c };
      // Cambiar el alcance invalida la decisión sobre los que ya tiene otra.
      const alcance: Array<keyof BorradorDeEstrategia> = [
        'forma',
        'categorias',
        'marcas',
        'skus',
        'excluidos',
      ];
      if (alcance.some((k) => k in c)) siguiente.onOverlap = null;
      return siguiente;
    });

  // El paso 4 sólo muestra: pide lo mismo que el 3 para crear.
  const pasoQueSeRevisa = paso === 4 ? 3 : paso;
  const falta = faltaEnElPaso(b, pasoQueSeRevisa);
  const faltaSolape = pasoQueSeRevisa === 3 && (solapados ?? 0) > 0 && b.onOverlap == null;
  const puedeSeguir =
    falta.length === 0 && !(pasoQueSeRevisa === 3 && (faltaSolape || solapados === null));
  const faltan = faltaSolape
    ? [...falta, 'Elegí qué hacer con los que ya tienen otra estrategia.']
    : falta;

  async function crear() {
    setGuardando(true);
    try {
      setFreno(null);
      const r = await strategyApplicationsApi.crear(cuerpoDeAlta(b)).catch((e: unknown) => {
        const delFreno = frenoDelError(e);
        if (!delFreno) throw e;
        setFreno(delFreno);
        return null;
      });
      if (!r) return;
      toast({
        title: 'Estrategia creada',
        description:
          r.noSePudoQuitar.length > 0
            ? `Ojo: ${r.noSePudoQuitar.length === 1 ? '1 producto sigue' : `${r.noSePudoQuitar.length} productos siguen`} con su estrategia anterior. ${r.noSePudoQuitar[0]}`
            : // Un solo aviso, que sirva desde la pestaña y desde Análisis (navegador T5:
              // Análisis sumaba el suyo y salían dos «Estrategia creada»).
              'Ya está en la pestaña Estrategias. Lo que proponga llega a Por decidir.',
        variant: r.noSePudoQuitar.length > 0 ? 'destructive' : undefined,
      });
      await onCreada();
    } catch (e) {
      toast({
        title: 'No se pudo crear',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      {/* C3 (27-sep): casi toda la pantalla, como «Nueva regla» (nueva-regla-dialog.tsx:137):
          el medio se desplaza y los botones quedan siempre a la vista. */}
      {/* Rediseño de Compite (15-oct), igual que «Nueva regla»: la píldora del módulo,
          los pasos, «Tu estrategia» al costado y los botones en una barra fija. */}
      <DialogContent className="flex h-[92vh] w-[96vw] max-w-[1600px] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-0 border-b px-6 pb-4 pt-5 text-left sm:px-8">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <SlidersHorizontal aria-hidden="true" className="h-3.5 w-3.5" />
            Compite · Estrategias
          </span>
          <DialogTitle className="tracking-tight pt-3 text-2xl font-semibold">
            {duplicaDe ? `Duplicar «${duplicaDe.nombre}»` : 'Crear una estrategia'}
          </DialogTitle>
          <DialogDescription className="pt-1">
            {paso === 4
              ? // Ronda final (A9), maqueta agregados.html#paso4: el resumen va de subtítulo.
                `«${b.nombre.trim() || 'Estrategia sin nombre'}» · ${resumenParaElSubtitulo(b)}`
              : duplicaDe
                ? 'Va todo copiado: a quién alcanza, las excepciones, cómo calcula, el permiso y cada cuánto revisa. Ponele un nombre y, si querés, una fecha de fin nueva.'
                : 'A quién alcanza, cómo calcula el precio y cuándo lo propone.'}
          </DialogDescription>
          {duplicaDe && duplicaDe.noSeCopia.length > 0 && (
            <p data-testid="no-se-copia" className="pt-2 text-xs text-alerta">
              La original tenía además {duplicaDe.noSeCopia.join(' y ')}: la copia no lo lleva.
            </p>
          )}
          {/* La misma pieza que «Nueva regla» (mapa 226, hallazgo 8). */}
          <PasosDelAsistente pasos={PASOS} actual={paso - 1} className="pt-4" />
        </DialogHeader>

        <div className="flex min-h-0 flex-1">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 sm:px-8">
            {paso === 1 && (
              <Paso1Alcance borrador={b} onChange={cambiar} desdeAnalisis={desdeAnalisis} />
            )}
            {paso === 2 && <Paso2Metodo borrador={b} onChange={cambiar} />}
            {paso === 3 && (
              <Paso3Cuando
                borrador={b}
                onChange={cambiar}
                onSolapados={setSolapados}
                avisoOriginalActiva={duplicaDe?.originalActiva === true}
              />
            )}
            {paso === 4 && <Paso4Resultados borrador={b} />}
          </div>

          <ResumenDelAsistente titulo="Tu estrategia">
            <FilaDelResumen
              titulo="Nombre"
              valor={b.nombre.trim() || null}
              vacio="Ponele un nombre en el paso 1."
            />
            <FilaDelResumen
              titulo="A quién"
              valor={aQuienEnPalabras(b)}
              vacio="Elegí categorías, marcas o productos."
            />
            <FilaDelResumen
              titulo="Cómo calcula"
              valor={
                paso >= 2 ? (METODOS.find((m) => m.metodo === b.metodo)?.titulo ?? null) : null
              }
              vacio="Lo elegís en el paso 2."
            />
            <FilaDelResumen
              titulo="Con qué permiso"
              valor={
                paso >= 3 && b.permiso
                  ? permisoEnPalabras(b.permiso.mode, b.permiso.askAboveChangePct)
                  : null
              }
              vacio="Lo elegís en el paso 3."
            />
            <FilaDelResumen
              titulo="Hasta cuándo"
              valor={
                paso >= 3
                  ? b.hastaCuando
                    ? `Hasta el ${formatoFecha(b.hastaCuando)}`
                    : 'Sin fecha de fin'
                  : null
              }
              vacio="Lo elegís en el paso 3."
            />
          </ResumenDelAsistente>
        </div>

        {freno && (
          <div className="border-t px-6 py-3 sm:px-8">
            <FrenoDeLaDemo {...freno} />
          </div>
        )}
        {/* Lo que falta para seguir se dice al lado del botón apagado (maqueta del pulido). */}
        <DialogFooter
          data-testid="pie-del-asistente"
          className="items-center gap-2 border-t bg-muted/30 px-6 py-3 sm:px-8"
        >
          {faltan.length > 0 ? (
            <p className="mr-auto text-xs text-muted-foreground">
              Falta: <span data-testid="falta">{faltan.map(minusculaInicial).join(' · ')}</span>
            </p>
          ) : (
            <p className="mr-auto text-xs text-muted-foreground">
              {paso === 4
                ? 'Nada se crea hasta que toques «Crear la estrategia».'
                : 'Nada se crea hasta el paso 4.'}
            </p>
          )}
          {/* Cambio aprobado 1 (spec 232): se sale también sin la ×, como en «Nueva regla». */}
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          {paso > 1 && (
            <Button
              variant="outline"
              onClick={() => {
                // C10 (27-sep): PermisoDeEstrategia guarda su estado adentro y se desmonta
                // al cambiar de paso. Un umbral inválido (permiso null) al volver se
                // dibujaría como «Siempre preguntame» con «Crear» trabado: vuelve al inicial.
                if (paso === 3 && b.permiso == null)
                  setB((x) => ({ ...x, permiso: PERMISO_INICIAL }));
                setPaso((p) => (p - 1) as 1 | 2 | 3);
              }}
            >
              Atrás
            </Button>
          )}
          {paso < 4 ? (
            <Button
              disabled={!puedeSeguir}
              onClick={() => setPaso((p) => (p + 1) as 2 | 3 | 4)}
              className={BOTON_DEGRADADO}
            >
              Siguiente: {PASOS[paso === 1 ? 1 : paso === 2 ? 2 : 3].toLowerCase()}
            </Button>
          ) : (
            <Button
              disabled={!puedeSeguir || guardando}
              onClick={() => void crear()}
              className={BOTON_DEGRADADO}
              {...sinCambios}
            >
              {guardando && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Crear la estrategia
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
